import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import {
  CohortQuestionDistributionsResponse,
  ProgramSurveyAnalyticsDTO,
  ProgramSurveysListResponse,
  SurveyCourseDetailDTO,
} from '@app/app/shared/services/survey-analytics/survey-analytics.model';
import { ObservableResult } from '@app/app/shared/store';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  AdminCoursesRequest,
  CohortGradeData,
  CohortOption,
  ProgramCourseItem,
  ProgramCoursesAnalyticsFilters,
  ProgramCoursesOverview,
  ProgramSkillsOverviewResponse,
  StudentEngagementData,
} from '../../models/courses-analytics.model';
import { CoursesAnalyticsDataService } from './courses-analytics-data.service';

@Injectable()
export class MockCoursesAnalyticsDataService implements CoursesAnalyticsDataService {
  private mockOverview: ProgramCoursesOverview = {
    members: 125,
    averageGrade: 75,
    averageGradeChange: 4,
    completion: 94,
    npsScore: 8.8,
    coursesCount: 15,
    activeCourses: 12,
    averagePreAssessment: 62,
    averagePostAssessment: 78,
    averageImprovement: 16,
    totalEnrolled: 125,
    totalInProgress: 42,
    totalCompleted: 83,
    surveyMetrics: [
      { metricType: 'NPS_SCORE', displayName: 'NPS Score', averageScore: 8.8, totalResponses: 95 },
      { metricType: 'PROFESSOR_RATING', displayName: 'Professor Rating', averageScore: 4.2, totalResponses: 110 },
      { metricType: 'COURSE_DESIGN_RATING', displayName: 'Course Design', averageScore: 3.9, totalResponses: 108 },
      { metricType: 'OVERALL_SATISFACTION', displayName: 'Overall Satisfaction', averageScore: 4.1, totalResponses: 112 },
    ],
    gradeDistribution: {
      buckets: [
        { label: 'A', min: 90, max: 100, count: 34 },
        { label: 'B', min: 80, max: 89, count: 42 },
        { label: 'C', min: 70, max: 79, count: 28 },
        { label: 'D', min: 60, max: 69, count: 14 },
        { label: 'F', min: 0, max: 59, count: 7 },
      ],
      totalStudents: 125,
      mean: 81.4,
      standardDeviation: 9.2,
    },
  };

  private mockCourses: ProgramCourseItem[] = [
    {
      id: '1',
      shadowPlaylistId: 'sp-1',
      courseName: 'Structural Engineering 201',
      instructor: { name: 'Elena Vance', imageUrl: undefined },
      cohortProgress: 72,
      status: 'Active',
      avgGrade: 72,
      nps: 8.8,
      included: true,
      preAssessment: 58,
      postAssessment: 74,
      finalGrade: 72,
      enrolled: 18,
      inProgress: 5,
      completed: 13,
      completionRate: 72,
      surveyMetrics: [
        { metricType: 'NPS_SCORE', displayName: 'NPS Score', averageScore: 8.8, totalResponses: 15 },
        { metricType: 'PROFESSOR_RATING', displayName: 'Professor Rating', averageScore: 4.3, totalResponses: 16 },
        { metricType: 'COURSE_DESIGN_RATING', displayName: 'Course Design', averageScore: 3.8, totalResponses: 14 },
        { metricType: 'OVERALL_SATISFACTION', displayName: 'Overall Satisfaction', averageScore: 4.0, totalResponses: 16 },
      ],
    },
    {
      id: '2',
      shadowPlaylistId: 'sp-2',
      courseName: 'Construction Safety',
      instructor: { name: 'John Doe' },
      cohortProgress: 85,
      status: 'Active',
      avgGrade: 95,
      nps: 7.2,
      included: true,
      preAssessment: 70,
      postAssessment: 90,
      finalGrade: 95,
      enrolled: 15,
      inProgress: 2,
      completed: 13,
      completionRate: 87,
    },
    {
      id: '3',
      shadowPlaylistId: 'sp-3',
      courseName: 'Advanced Fluid Mechanics',
      instructor: { name: 'Joe Puffer' },
      cohortProgress: 60,
      status: 'Ended',
      avgGrade: 88,
      nps: 9.3,
      included: true,
      preAssessment: 65,
      postAssessment: 82,
      finalGrade: 88,
      enrolled: 12,
      inProgress: 0,
      completed: 12,
      completionRate: 100,
    },
    {
      id: '4',
      shadowPlaylistId: 'sp-4',
      courseName: 'Thermal Systems',
      instructor: { name: 'Alice Smith' },
      cohortProgress: 78,
      status: 'Active',
      avgGrade: 92,
      nps: 8.5,
      included: true,
      preAssessment: 68,
      postAssessment: 85,
      finalGrade: 92,
      enrolled: 14,
      inProgress: 3,
      completed: 11,
      completionRate: 79,
    },
    {
      id: '5',
      shadowPlaylistId: 'sp-5',
      courseName: 'Reinforced Concrete Design',
      instructor: { name: 'Bob Brown' },
      cohortProgress: 65,
      status: 'Ended',
      avgGrade: 85,
      nps: 7.8,
      included: true,
      preAssessment: 55,
      postAssessment: 78,
      finalGrade: 85,
      enrolled: 10,
      inProgress: 0,
      completed: 10,
      completionRate: 100,
    },
    {
      id: '6',
      shadowPlaylistId: 'sp-6',
      courseName: 'Soil Mechanics',
      instructor: { name: 'Catherine White' },
      cohortProgress: 82,
      status: 'Active',
      avgGrade: 90,
      nps: 8.1,
      included: true,
      preAssessment: 62,
      postAssessment: 80,
      finalGrade: 90,
      enrolled: 16,
      inProgress: 3,
      completed: 13,
      completionRate: 81,
    },
    {
      id: '7',
      shadowPlaylistId: 'sp-7',
      courseName: 'Highway Design',
      instructor: { name: 'David Green' },
      cohortProgress: 70,
      status: 'Ended',
      avgGrade: 87,
      nps: 7.0,
      included: true,
      preAssessment: 60,
      postAssessment: 76,
      finalGrade: 87,
      enrolled: 11,
      inProgress: 0,
      completed: 11,
      completionRate: 100,
    },
    {
      id: '8',
      shadowPlaylistId: 'sp-8',
      courseName: 'Construction Project Management',
      instructor: { name: 'Emily Black' },
      cohortProgress: 90,
      status: 'Active',
      avgGrade: 93,
      nps: 9.0,
      included: true,
      preAssessment: 72,
      postAssessment: 88,
      finalGrade: 93,
      enrolled: 13,
      inProgress: 1,
      completed: 12,
      completionRate: 92,
    },
    {
      id: '9',
      shadowPlaylistId: 'sp-9',
      courseName: 'Renewable Energy Systems',
      instructor: { name: 'Frank Blue' },
      cohortProgress: 55,
      status: 'Ended',
      avgGrade: 84,
      nps: 7.5,
      included: true,
      preAssessment: 52,
      postAssessment: 72,
      finalGrade: 84,
      enrolled: 9,
      inProgress: 0,
      completed: 9,
      completionRate: 100,
    },
    {
      id: '10',
      shadowPlaylistId: 'sp-10',
      courseName: 'Environmental Impact Assessment',
      instructor: { name: 'Grace Silver' },
      cohortProgress: 45,
      status: 'Active',
      avgGrade: 78,
      nps: 6.8,
      included: true,
      preAssessment: 48,
      postAssessment: 68,
      finalGrade: 78,
      enrolled: 7,
      inProgress: 4,
      completed: 3,
      completionRate: 43,
    },
  ];

  private mockCohortGradeData: CohortGradeData[] = [
    {
      label: 'Cybersecurity',
      cohorts: [
        { name: 'ZAIN 2024 S7', value: 82.5 },
        { name: 'ARAMCO S2', value: 78.3 },
        { name: 'Spring 2025', value: 85.1 },
      ],
    },
    {
      label: 'Data Analytics',
      cohorts: [
        { name: 'ZAIN 2024 S7', value: 75.2 },
        { name: 'ARAMCO S2', value: 80.1 },
        { name: 'Spring 2025', value: 77.8 },
      ],
    },
    {
      label: 'Project Management',
      cohorts: [
        { name: 'ZAIN 2024 S7', value: 88.0 },
        { name: 'ARAMCO S2', value: 84.5 },
        { name: 'Spring 2025', value: 90.2 },
      ],
    },
    {
      label: 'Leadership',
      cohorts: [
        { name: 'ZAIN 2024 S7', value: 91.3 },
        { name: 'ARAMCO S2', value: 87.6 },
        { name: 'Spring 2025', value: 89.0 },
      ],
    },
  ];

  private mockEngagement: StudentEngagementData = {
    totalTimeSpent: 54000,
    uniqueStudents: 75,
    averageTimeSpentPerUser: 720,
  };

  private mockCohorts: CohortOption[] = [
    { id: 'ZAIN 2024 S7', name: 'ZAIN 2024 S7', company: 'ZAIN', courseCount: 14, dateRange: 'Jan 2024 - Jun 2024' },
    { id: 'ARAMCO S2', name: 'ARAMCO S2', company: 'ARAMCO', courseCount: 12, dateRange: 'Feb 2024 - Jul 2024' },
    { id: 'Spring 2025', name: 'Spring 2025', company: 'ZAIN', courseCount: 10, dateRange: 'Jan 2025 - May 2025' },
  ];

  getAdminCourses(params?: AdminCoursesRequest): ObservableResult<Page<ProgramItem>> {
    return ObservableResult.ofSuccess();
  }

  getOverview(_programId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCoursesOverview> {
    return of({ isSuccess: true, value: this.mockOverview }).pipe(delay(300)) as ObservableResult<ProgramCoursesOverview>;
  }

  getOverviewByOrgId(
    _clientOrgId: string,
    _programId: string,
    _filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCoursesOverview> {
    return of({ isSuccess: true, value: this.mockOverview }).pipe(delay(300)) as ObservableResult<ProgramCoursesOverview>;
  }

  getCourses(_programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCourseItem[]> {
    let courses = [...this.mockCourses];

    if (filters?.statusFilter && filters.statusFilter !== 'all') {
      const status = filters.statusFilter === 'active' ? 'Active' : 'Ended';
      courses = courses.filter((c) => c.status === status);
    }

    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      courses = courses.filter((c) => c.courseName.toLowerCase().includes(query) || c.instructor.name.toLowerCase().includes(query));
    }

    return of({ isSuccess: true, value: courses }).pipe(delay(300)) as ObservableResult<ProgramCourseItem[]>;
  }

  getCohortGradeComparison(_programId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<CohortGradeData[]> {
    return of({ isSuccess: true, value: this.mockCohortGradeData }).pipe(delay(300)) as ObservableResult<CohortGradeData[]>;
  }

  getStudentEngagement(_programId: string, _filters?: ProgramCoursesAnalyticsFilters): ObservableResult<StudentEngagementData> {
    return of({ isSuccess: true, value: this.mockEngagement }).pipe(delay(300)) as ObservableResult<StudentEngagementData>;
  }
  getCohorts(_programId: string): ObservableResult<CohortOption[]> {
    return of({ isSuccess: true, value: this.mockCohorts }).pipe(delay(200)) as ObservableResult<CohortOption[]>;
  }

  getProgramSkillsOverview(_programId: string): ObservableResult<ProgramSkillsOverviewResponse> {
    const mock: ProgramSkillsOverviewResponse = {
      totalMembers: 92,
      skills: [
        {
          tagUid: 'tag-001',
          tagTitle: 'Strategic thinking',
          moduleUids: ['mod-001', 'mod-002'],
          levels: [
            { levelUid: 'lvl-1', levelTitle: 'Beginner', levelRank: 0, moduleUids: ['mod-001'], membersAchievedCount: 85 },
            { levelUid: 'lvl-2', levelTitle: 'Proficient', levelRank: 1, moduleUids: ['mod-001'], membersAchievedCount: 70 },
            { levelUid: 'lvl-3', levelTitle: 'Advanced', levelRank: 2, moduleUids: ['mod-001'], membersAchievedCount: 42 },
            { levelUid: 'lvl-4', levelTitle: 'Mastery', levelRank: 3, moduleUids: [], membersAchievedCount: 0 },
          ],
        },
        {
          tagUid: 'tag-002',
          tagTitle: 'Digital fluency',
          moduleUids: ['mod-003'],
          levels: [
            { levelUid: 'lvl-1', levelTitle: 'Beginner', levelRank: 0, moduleUids: ['mod-003'], membersAchievedCount: 80 },
            { levelUid: 'lvl-2', levelTitle: 'Proficient', levelRank: 1, moduleUids: ['mod-003'], membersAchievedCount: 60 },
            { levelUid: 'lvl-3', levelTitle: 'Advanced', levelRank: 2, moduleUids: ['mod-003'], membersAchievedCount: 30 },
            { levelUid: 'lvl-4', levelTitle: 'Mastery', levelRank: 3, moduleUids: [], membersAchievedCount: 0 },
          ],
        },
        {
          tagUid: 'tag-003',
          tagTitle: 'Leadership',
          moduleUids: ['mod-001', 'mod-004'],
          levels: [
            { levelUid: 'lvl-1', levelTitle: 'Beginner', levelRank: 0, moduleUids: ['mod-001', 'mod-004'], membersAchievedCount: 88 },
            { levelUid: 'lvl-2', levelTitle: 'Proficient', levelRank: 1, moduleUids: ['mod-001'], membersAchievedCount: 55 },
            { levelUid: 'lvl-3', levelTitle: 'Advanced', levelRank: 2, moduleUids: [], membersAchievedCount: 0 },
          ],
        },
        {
          tagUid: 'tag-004',
          tagTitle: 'Data-driven decision making',
          moduleUids: ['mod-002'],
          levels: [
            { levelUid: 'lvl-1', levelTitle: 'Beginner', levelRank: 0, moduleUids: ['mod-002'], membersAchievedCount: 72 },
            { levelUid: 'lvl-2', levelTitle: 'Proficient', levelRank: 1, moduleUids: [], membersAchievedCount: 0 },
          ],
        },
        {
          tagUid: 'tag-005',
          tagTitle: 'Communication',
          moduleUids: ['mod-003', 'mod-005'],
          levels: [
            { levelUid: 'lvl-1', levelTitle: 'Beginner', levelRank: 0, moduleUids: ['mod-003', 'mod-005'], membersAchievedCount: 90 },
            { levelUid: 'lvl-2', levelTitle: 'Proficient', levelRank: 1, moduleUids: ['mod-003'], membersAchievedCount: 65 },
            { levelUid: 'lvl-3', levelTitle: 'Advanced', levelRank: 2, moduleUids: [], membersAchievedCount: 0 },
          ],
        },
      ],
      skillsByModule: [
        { moduleUid: 'mod-001', moduleTitle: 'Strategic Leadership', tagUids: ['tag-001', 'tag-003'] },
        { moduleUid: 'mod-002', moduleTitle: 'Digital Transformation', tagUids: ['tag-001', 'tag-004'] },
        { moduleUid: 'mod-003', moduleTitle: 'Communication & Influence', tagUids: ['tag-002', 'tag-005'] },
        { moduleUid: 'mod-004', moduleTitle: 'Leading Teams', tagUids: ['tag-003'] },
        { moduleUid: 'mod-005', moduleTitle: 'Stakeholder Management', tagUids: ['tag-005'] },
      ],
    };
    return of({ isSuccess: true, value: mock }).pipe(delay(300)) as ObservableResult<ProgramSkillsOverviewResponse>;
  }

  getCoursesByOrgId(
    _clientOrgId: string,
    _programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<ProgramCourseItem[]> {
    return this.getCourses(_programId, filters);
  }

  getCohortGradeComparisonByOrgId(
    _clientOrgId: string,
    _programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<CohortGradeData[]> {
    return this.getCohortGradeComparison(_programId, filters);
  }

  getStudentEngagementByOrgId(
    _clientOrgId: string,
    _programId: string,
    filters?: ProgramCoursesAnalyticsFilters,
  ): ObservableResult<StudentEngagementData> {
    return this.getStudentEngagement(_programId, filters);
  }

  getCohortsByOrgId(_clientOrgId: string, _programId: string): ObservableResult<CohortOption[]> {
    return this.getCohorts(_programId);
  }

  getSurveyAnalyticsByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: { cohortNames?: string[]; metricType?: string; company?: string },
  ): ObservableResult<ProgramSurveyAnalyticsDTO> {
    return ObservableResult.ofSuccess();
  }

  getProgramSurveysListByOrgId(clientOrgId: string, programUid: string, client?: string): ObservableResult<ProgramSurveysListResponse> {
    return ObservableResult.ofSuccess();
  }

  getQuestionDistributionsByOrgId(
    clientOrgId: string,
    programId: string,
    filters?: { cohortNames?: string[]; courseNames?: string[]; company?: string },
  ): ObservableResult<CohortQuestionDistributionsResponse> {
    return ObservableResult.ofSuccess();
  }

  getCourseDetailByOrgId(
    clientOrgId: string,
    programId: string,
    courseName: string,
    cohortNames?: string[],
    company?: string,
  ): ObservableResult<SurveyCourseDetailDTO> {
    return ObservableResult.ofSuccess();
  }
}
